import { useEffect, useRef, useState } from 'react'
import { getOnboardingFirstStepRoute } from '../onboardingBrowserUtils'
import { useNavigate } from 'react-router-dom'
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers'
import { useAppActions } from '../../../stores/application/useAppActions'
import { useAppStore } from '../../../stores/application/useAppStore'

const UNINSTALL_CHECK_INTERVAL_MS = 3000

/**
 * On onboarding steps that require the Chrome extension: detect when the
 * extension is missing (e.g. uninstalled) and send the user back to install.
 */
export const useOnboardingExtensionRequired = (enabled = true) => {
    const { checkExtensionStatus } = useAppHandlers()
    const { setOnboardingStep } = useAppActions()
    const { store: { extensionInstalled } } = useAppStore()
    const navigate = useNavigate()
    const redirectingRef = useRef(false)
    const checkInFlightRef = useRef(false)
    const [initialCheckDone, setInitialCheckDone] = useState(false)

    const redirectToExtensionInstall = () => {
        if (redirectingRef.current) return
        redirectingRef.current = true
        setOnboardingStep(1)
        navigate(getOnboardingFirstStepRoute(), { replace: true })
    }

    const runExtensionCheck = () => {
        if (checkInFlightRef.current) return
        checkInFlightRef.current = true
        void checkExtensionStatus().finally(() => {
            checkInFlightRef.current = false
        })
    }

    useEffect(() => {
        if (!enabled) return

        let cancelled = false
        void checkExtensionStatus().finally(() => {
            if (!cancelled) setInitialCheckDone(true)
        })

        return () => {
            cancelled = true
        }
    }, [enabled, checkExtensionStatus])

    useEffect(() => {
        if (!enabled || !initialCheckDone || extensionInstalled) return
        redirectToExtensionInstall()
    }, [enabled, initialCheckDone, extensionInstalled])

    useEffect(() => {
        if (!enabled) return

        const onTabActivated = () => {
            if (document.visibilityState !== 'visible') return
            runExtensionCheck()
        }

        window.addEventListener('focus', onTabActivated)
        document.addEventListener('visibilitychange', onTabActivated)

        return () => {
            window.removeEventListener('focus', onTabActivated)
            document.removeEventListener('visibilitychange', onTabActivated)
        }
    }, [enabled])

    useEffect(() => {
        if (!enabled || !extensionInstalled) return

        const intervalId = window.setInterval(runExtensionCheck, UNINSTALL_CHECK_INTERVAL_MS)

        return () => window.clearInterval(intervalId)
    }, [enabled, extensionInstalled])
}
